import {
	loadConversationFeature,
	clearConversationFeature,
} from '@/api/query/feature';
import { findScenceByVersionForCustomer } from '@/api/query/scene';
import { useState, useMemo, useEffect } from 'react';
import { useMutation } from 'react-query';
import { union } from '../helper/union';
import { Scene } from '../types/Scene';
import { useNavigate } from 'react-router-dom';

const useSceneV3 = (id: string) => {
	const navigate = useNavigate();

	const [sceneContent, setSceneContent] = useState<Scene>();
	const [conversation, setConversation] = useState();
	const [completedTasks, setCompletedTasks] = useState<number[]>([]);
	const [isBackPopupShown, setIsBackPopupShown] = useState(false);
	const [isTasksListShown, setIsTasksListShown] = useState(false);
	const [isTutorialVisible, setIsTutorialVisible] = useState(false);
	const [isRateFeaturePopUpVisible, setIsRateFeaturePopUpVisible] =
		useState(false);
	const [hasEnded, setHasEnded] = useState(false);

	const handleLoadLessonMutation = useMutation({
		mutationFn: (value: any) => findScenceByVersionForCustomer(value),
		onSuccess: ({ data }) => {
			setSceneContent(data);
		},
	});

	const handleLoadConversation = useMutation({
		mutationFn: (payload: any) => loadConversationFeature(payload),
		onSuccess: ({ data }) => {
			setConversation(data);
		},
	});

	const handleClearConversation = useMutation({
		mutationFn: (payload: any) => clearConversationFeature(payload),
		onSuccess: ({ data }) => {
			setConversation(data);
		},
	});

	const scenePayload = useMemo(
		() => ({ _id: id, version: 'V3', type: 'SCENE', sceneId: id }),
		[id],
	);

	const handleRefreshFunction = () => {
		handleClearConversation.mutate(scenePayload);
	};

	const backHandler = () => {
		const hasRated = localStorage.getItem('scene-v3-rated');
		if (!hasRated) {
			setIsRateFeaturePopUpVisible(true);
			localStorage.setItem('scene-v3-rated', '1');
			return;
		}

		handleRefreshFunction();
		navigate('/learn/scenes-v3');
	};

	const handleCloseTutorial = () => {
		localStorage.setItem('scene-v3-tutorial', 'true');
		setIsTutorialVisible(false);
	};

	const handleCompletedTasksChange = (tasks: number[]) => {
		const completedTasksUnion = union<number>(completedTasks, tasks).sort();
		const sceneTasks = sceneContent?.tasks?.length || 0;

		if (
			completedTasksUnion.length > completedTasks.length &&
			completedTasksUnion.length < sceneTasks
		) {
			setTimeout(() => {
				setIsTasksListShown(true);
			}, 100);
		}

		setCompletedTasks(completedTasksUnion);
	};

	useEffect(() => {
		const isTutorialShowedBefore = !!localStorage.getItem('scene-v3-tutorial');
		setIsTutorialVisible(!isTutorialShowedBefore);
	}, []);

	useEffect(() => {
		if (completedTasks?.length === sceneContent?.tasks?.length && !hasEnded) {
			handleRefreshFunction();
			setHasEnded(true);
		}
	}, [completedTasks, sceneContent]);

	useEffect(() => {
		window.document.title = `Synthi Ai - Scene`;

		if (!id) return;
		handleLoadConversation.mutate(scenePayload);
		handleLoadLessonMutation.mutate(scenePayload);
	}, [id]);

	return {
		sceneContent,
		conversation,
		completedTasks,
		isBackPopupShown,
		setIsBackPopupShown,
		isTasksListShown,
		setIsTasksListShown,
		isTutorialVisible,
		setIsTutorialVisible,
		isRateFeaturePopUpVisible,
		setIsRateFeaturePopUpVisible,
		hasEnded,
		setHasEnded,
		handleLoadLessonMutation,
		handleLoadConversation,
		handleClearConversation,
		handleRefreshFunction,
		backHandler,
		handleCloseTutorial,
		handleCompletedTasksChange,
		scenePayload,
		isLoading:
			handleLoadLessonMutation.isLoading ||
			handleLoadConversation.isLoading ||
			handleClearConversation.isLoading,
	};
};

export default useSceneV3;
